import { inject, Injectable } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { FormValidators } from '@core/data-access/models/dynamic-form-field/dynamic-form-field-validators.model';
import {
  CheckboxField,
  DynamicFormField,
  DynamicFormFields,
  FieldKey,
  OptionKey
} from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import {
  CheckboxFormField,
  CheckboxFormFieldValues,
  FormField,
  FormFields,
  FormFieldsValue,
  FormFieldValue
} from '@core/data-access/models/form/form-store.model';
import { DynamicFormFieldValidatorFactory } from '@core/data-access/services/dynamic-form-field/dynamic-form-field-validator-factory.service';
import { isFieldDisabled, isFieldHidden } from '@core/utils/dynamic-form-field/dynamic-form-field-helpers';
import {
  isCheckboxField,
  isDateInputField,
  isDropdownField,
  isQuestionnaireCheckboxField,
  isRadioField,
  isTextInputField
} from '@core/utils/dynamic-form-field/dynamic-form-field-type-guards';
import {
  checkFieldValueTranslationsAndSetToFieldOptionValue,
  checkOptionValueAndSetToTranslatedFieldValue,
  combineTranslatedFormOptionKeyAndValue
} from '@core/utils/form-helpers';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, find, findKey, forEach, isEmpty, isNil, isNull, keys, reduce } from 'lodash';
import { DynamicFieldOptionsTranslationFactory } from './dynamic-field-options-translation-factory.service';

@Injectable({
  providedIn: 'root'
})
export class DynamicFormFactory {
  readonly #translateService = inject(TranslateService);
  readonly #dynamicFormFieldValidatorFactory = inject(DynamicFormFieldValidatorFactory);
  readonly #dynamicFieldOptionsTranslationFactory = inject(DynamicFieldOptionsTranslationFactory);

  createForm(dynamicFormFields: DynamicFormFields, formFields: FormFields) {
    const formGroup = new FormGroup({});

    const formValidators: FormValidators = {
      validators: [],
      asyncValidators: []
    };

    forEach(dynamicFormFields, (dynamicFormField) => {
      const dynamicFormFieldKey = dynamicFormField.key;
      const formField = formFields[dynamicFormFieldKey];

      this.setIsFieldHidden(dynamicFormField, formFields);

      if (isCheckboxField(dynamicFormField)) {
        const checkboxFormGroup = this.createCheckboxFieldFormGroup(
          dynamicFormField,
          formGroup,
          formValidators,
          formField
        );

        formGroup.addControl(dynamicFormFieldKey, checkboxFormGroup);
      } else {
        const fieldValue = this.setPrefilledFieldValue(dynamicFormField, formField);
        const isDisabled = isFieldDisabled(dynamicFormField, formFields);
        const formControlValidators = this.#dynamicFormFieldValidatorFactory.createDynamicFieldValidators(
          dynamicFormField,
          formGroup,
          formValidators
        );

        const formControl = new FormControl({ value: fieldValue, disabled: isDisabled }, formControlValidators);

        formGroup.addControl(dynamicFormFieldKey, formControl);
      }
    });

    formGroup.addValidators(formValidators.validators);
    formGroup.addAsyncValidators(formValidators.asyncValidators);

    return formGroup;
  }

  updateForm(dynamicFormFields: DynamicFormFields, formFields: FormFields, formGroup: FormGroup) {
    const clonedDynamicFormFields = cloneDeep(dynamicFormFields);

    forEach(keys(formFields), (fieldKey) => {
      const clonedDynamicFormField = find(clonedDynamicFormFields, ({ key }) => key === fieldKey);
      const formField = formFields[fieldKey];

      if (isNil(clonedDynamicFormField) || isNil(formField)) {
        return;
      }

      const { value, isDisabled } = formField;

      const fieldFormControl = formGroup.get(fieldKey);

      this.setIsFieldDisabled(clonedDynamicFormField, formFields, fieldFormControl);
      this.setIsFieldHidden(clonedDynamicFormField, formFields);

      if (isCheckboxField(clonedDynamicFormField)) {
        const checkboxFormGroup: FormGroup = fieldFormControl as FormGroup;
        const checkboxFormFieldValues: CheckboxFormFieldValues = value;
        const areAllCheckboxOptionsDisabled: boolean = isDisabled;

        this.setCheckboxFieldValidators(clonedDynamicFormField, checkboxFormGroup);

        forEach(keys(checkboxFormFieldValues), (optionKey: OptionKey) => {
          const checkboxFormField = checkboxFormFieldValues[optionKey];

          const { value: optionValue, isDisabled: isOptionDisabled } = checkboxFormField;

          const checkboxOptionFormControl = checkboxFormGroup.get(optionKey);

          this.setFormControlValue(optionValue, checkboxOptionFormControl);
          this.setIsCheckboxOptionFieldDisabled(
            areAllCheckboxOptionsDisabled || isOptionDisabled,
            checkboxOptionFormControl
          );
        });

        this.disableUncheckedCheckboxesWhenSelectionLimitIsReached(clonedDynamicFormField, checkboxFormGroup);
      } else {
        const fieldValue = this.setPrefilledFieldValue(clonedDynamicFormField, formField);

        this.setFormControlValue(fieldValue, fieldFormControl);
      }
    });

    return clonedDynamicFormFields;
  }

  setFormEditable(
    isFormEditable: boolean,
    formGroup: FormGroup,
    skipFields: {
      enableForm?: FieldKey[];
      disableForm?: FieldKey[];
    } = {
      enableForm: [],
      disableForm: []
    }
  ) {
    isFormEditable
      ? this.enableForm(formGroup, skipFields.enableForm)
      : this.disableForm(formGroup, skipFields.disableForm);
  }

  enableForm(formGroup: FormGroup, skipFields: FieldKey[] = []) {
    formGroup.enable();

    forEach(skipFields, (fieldKey) => formGroup.get(fieldKey).disable());
  }

  disableForm(formGroup: FormGroup, skipFields: FieldKey[] = []) {
    formGroup.disable();

    forEach(skipFields, (fieldKey) => formGroup.get(fieldKey).enable());
  }

  private createCheckboxFieldFormGroup(
    checkboxField: CheckboxField,
    formGroup: FormGroup,
    formValidators: FormValidators,
    checkboxFormField: CheckboxFormField
  ) {
    const { isHidden, options } = checkboxField;
    const checkboxFormGroupValidators = this.#dynamicFormFieldValidatorFactory.createDynamicFieldValidators(
      checkboxField,
      formGroup,
      formValidators
    );

    const checkboxFormGroup = new FormGroup({}, checkboxFormGroupValidators);

    forEach(options, (option) => {
      const { key, value, isDisabled } = option;

      const checkboxFormFieldExists = !isNil(checkboxFormField);

      const checkboxOptionFormField = checkboxFormField?.value?.[key];

      const fieldValue: FormFieldsValue = isNil(checkboxOptionFormField) ? value : checkboxOptionFormField.value;

      const areAllCheckboxOptionsDisabled = checkboxFormFieldExists && checkboxFormField.isDisabled;
      const isFieldDisabled = checkboxFormFieldExists
        ? areAllCheckboxOptionsDisabled || checkboxOptionFormField?.isDisabled
        : isDisabled;

      const formControlValidators = this.#dynamicFormFieldValidatorFactory.createCheckboxOptionValidators(
        isHidden,
        option
      );

      const formControl = new FormControl({ value: fieldValue, disabled: isFieldDisabled }, formControlValidators);

      checkboxFormGroup.addControl(key.toString(), formControl);
    });

    this.disableUncheckedCheckboxesWhenSelectionLimitIsReached(checkboxField, checkboxFormGroup);

    return checkboxFormGroup;
  }

  private setIsFieldDisabled(dynamicFormField: DynamicFormField, formFields: FormFields, formControl: AbstractControl) {
    const isDisabled = isFieldDisabled(dynamicFormField, formFields);

    if (isDisabled === formControl?.disabled) {
      return;
    }

    isDisabled ? formControl.disable() : formControl.enable();
  }

  private setIsFieldHidden(dynamicFormField: DynamicFormField, formFields: FormFields) {
    dynamicFormField.isHidden = isFieldHidden(dynamicFormField, formFields);
  }

  /**
   * Checkbox validators are required-ness is gated on `isHidden` (unlike other field types, which rely solely on
   * disabling the control to exclude a hidden field from the form's validity). That gate is only evaluated once,
   * when the control is created, so a questionnaire checkbox revealed later by another field's answer needs its
   * required validators re-derived here against its current visibility, or it would never become invalid.
   */
  private setCheckboxFieldValidators(checkboxField: CheckboxField, checkboxFormGroup: FormGroup) {
    const checkboxFormGroupValidators = this.#dynamicFormFieldValidatorFactory.createDynamicFieldValidators(
      checkboxField,
      checkboxFormGroup,
      { validators: [], asyncValidators: [] }
    );

    checkboxFormGroup.setValidators(checkboxFormGroupValidators.validators);
    checkboxFormGroup.setAsyncValidators(checkboxFormGroupValidators.asyncValidators);

    forEach(checkboxField.options, (option) => {
      const optionFormControl = checkboxFormGroup.get(option.key.toString());
      const optionValidators = this.#dynamicFormFieldValidatorFactory.createCheckboxOptionValidators(
        checkboxField.isHidden,
        option
      );

      optionFormControl?.setValidators(optionValidators.validators);
      optionFormControl?.setAsyncValidators(optionValidators.asyncValidators);
      optionFormControl?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    });

    checkboxFormGroup.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  private disableUncheckedCheckboxesWhenSelectionLimitIsReached(
    dynamicFormField: DynamicFormField,
    checkboxFormGroup: FormGroup
  ) {
    if (!isQuestionnaireCheckboxField(dynamicFormField)) {
      return;
    }

    if (isNil(dynamicFormField?.selectionLimit)) {
      return;
    }

    const { checkedFieldKeys, uncheckedFieldKeys } = reduce(
      checkboxFormGroup.getRawValue(),
      (checkboxes, isChecked, fieldKey) => {
        isChecked ? checkboxes.checkedFieldKeys.push(fieldKey) : checkboxes.uncheckedFieldKeys.push(fieldKey);

        return checkboxes;
      },
      {
        checkedFieldKeys: [],
        uncheckedFieldKeys: []
      }
    );

    const isDisabled = checkedFieldKeys.length >= dynamicFormField.selectionLimit;

    forEach(uncheckedFieldKeys, (fieldKey) => {
      this.setIsCheckboxOptionFieldDisabled(isDisabled, checkboxFormGroup.get(fieldKey));
    });
  }

  private setIsCheckboxOptionFieldDisabled(isOptionDisabled: boolean, formControl: AbstractControl) {
    isOptionDisabled ? formControl?.disable() : formControl?.enable();
  }

  private setFormControlValue(fieldValue: FormFieldValue, formControl: AbstractControl) {
    if (isNil(formControl) || fieldValue === formControl.value) {
      return;
    }

    formControl.setValue(fieldValue);
  }

  private setPrefilledFieldValue(dynamicFormField: DynamicFormField, formField: FormField) {
    if (isNil(formField?.value)) {
      return '';
    }

    const { value } = formField;

    if (isTextInputField(dynamicFormField)) {
      return checkOptionValueAndSetToTranslatedFieldValue(
        value as string,
        dynamicFormField.key,
        this.#translateService
      );
    }

    if (isDropdownField(dynamicFormField) || isRadioField(dynamicFormField)) {
      const { key, options } = dynamicFormField;

      const optionValue = combineTranslatedFormOptionKeyAndValue(key, value);

      const fieldValueExistsInOptions = findKey(options, { value: optionValue });

      if (fieldValueExistsInOptions) {
        return optionValue;
      }

      return checkFieldValueTranslationsAndSetToFieldOptionValue(
        value as string,
        key,
        this.#translateService,
        this.#dynamicFieldOptionsTranslationFactory
      );
    }

    if (isDateInputField(dynamicFormField)) {
      return isNull(value) || isEmpty(value) ? value : new Date(value as string);
    }

    return value;
  }
}
